import React, { useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../utils/supabaseClient';

interface DiagnosticResult {
  configured: boolean;
  authenticated: boolean;
  canRead: boolean;
  canWrite: boolean;
  error?: string;
}

export const SupabaseDiagnostics: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [result, setResult] = useState<DiagnosticResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const runDiagnostics = async () => {
      const diagnostic: DiagnosticResult = {
        configured: isSupabaseConfigured,
        authenticated: false,
        canRead: false,
        canWrite: false,
      };

      try {
        // Check authentication
        const { data: { session } } = await supabase.auth.getSession();
        diagnostic.authenticated = !!session?.user;

        // Test read access
        try {
          const { data, error } = await supabase
            .from('videos')
            .select('id')
            .limit(1);
          
          if (error) {
            diagnostic.error = `Read error: ${error.message}`;
          } else {
            diagnostic.canRead = true;
          }
        } catch (e: any) {
          diagnostic.error = `Read exception: ${e.message}`;
        }

        // Test write access (if authenticated)
        if (diagnostic.authenticated) {
          try {
            const testData = {
              platform: 'Other',
              video_url: 'test://diagnostic',
              title: 'Diagnostic Test',
              creator_name: 'Test',
              restaurant_name: 'Test Restaurant',
              user_id: session?.user?.id,
            };

            const { error: insertError } = await supabase
              .from('videos')
              .insert([testData])
              .select();

            if (insertError) {
              diagnostic.error = `Write error: ${insertError.message}`;
            } else {
              diagnostic.canWrite = true;
              
              // Clean up test record
              await supabase
                .from('videos')
                .delete()
                .eq('video_url', 'test://diagnostic');
            }
          } catch (e: any) {
            diagnostic.error = `Write exception: ${e.message}`;
          }
        }

      } catch (e: any) {
        diagnostic.error = `Auth error: ${e.message}`;
      }

      setResult(diagnostic);
      setLoading(false);
    };

    runDiagnostics();
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <h3 className="text-lg font-semibold mb-4">Running Supabase Diagnostics...</h3>
          <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Supabase Diagnostics</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        
        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${result.configured ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Configuration: {result.configured ? 'OK' : 'Missing env vars'}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${result.authenticated ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
              <span>Authentication: {result.authenticated ? 'Logged in' : 'Not logged in'}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${result.canRead ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>Read Access: {result.canRead ? 'OK' : 'Failed'}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${result.canWrite ? 'bg-green-500' : result.authenticated ? 'bg-red-500' : 'bg-gray-500'}`}></span>
              <span>Write Access: {result.canWrite ? 'OK' : result.authenticated ? 'Failed' : 'N/A (not logged in)'}</span>
            </div>
            
            {result.error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-700 font-mono">{result.error}</p>
              </div>
            )}
          </div>
        )}
        
        <div className="mt-6 text-sm text-gray-600">
          <p><strong>Next steps if issues found:</strong></p>
          <ul className="list-disc ml-4 mt-2 space-y-1">
            <li>Check Supabase CORS settings</li>
            <li>Verify RLS policies are applied</li>
            <li>Ensure user is properly authenticated</li>
            <li>Check Cloudflare environment variables</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SupabaseDiagnostics;
